import { memo } from 'react';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/atoms/Button';
import { EmptyState } from '@/components/atoms';

/**
 * Empty state for `/classroom/course/:subjectId` (and child routes) when
 * the subject id isn't a real course — either malformed (e.g.
 * "not-a-uuid") or a well-shaped UUID that doesn't match any row.
 *
 * Replaces two prior failure modes that /qa surfaced 2026-04-25:
 *   - malformed UUID → ErrorBoundary "Something went wrong" (RPC 400)
 *   - non-existent UUID → CoursePaywall with empty title and a misleading
 *     "Enroll for Free" button targeting a course that doesn't exist
 *
 * Rendered inside CourseOverview / CourseLayout so it inherits the same
 * top bar (Back to Classroom). Keeps the user oriented inside the app
 * rather than dumping them in a chromeless error card.
 */
export const CourseNotFound = memo(function CourseNotFound() {
  return (
    <div className="flex flex-col h-full bg-background">
      <div className="h-12 w-full border-b border-border flex items-center gap-3 px-4 shrink-0 bg-background">
        <Button variant="ghost" size="sm" asChild aria-label="Back to Classroom">
          <Link to="/classroom">
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Classroom</span>
          </Link>
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <EmptyState
            icon={BookOpen}
            title="Course not found"
            description="We couldn't find this course. It may have been removed, or the link you followed is incorrect."
            action={
              <Button asChild>
                <Link to="/classroom">Browse your classroom</Link>
              </Button>
            }
          />
        </div>
      </div>
    </div>
  );
});

CourseNotFound.displayName = 'CourseNotFound';
